import { motion } from "framer-motion";
import { Button } from "../ui/button";
import { Download, ArrowRight, Sparkles, Award, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import InquiryForm from "./InquiryForm";
import { COLLEGE, IMAGES } from "../../lib/constants";

const Hero = () => {
  const onBrochure = () => {
    toast.info("Brochure coming soon — request a printed copy from our counsellor.");
  };

  return (
    <section id="top" className="relative pt-28 md:pt-32 pb-16 md:pb-24 overflow-hidden bg-megha-primary" data-testid="section-hero">
      {/* Background */}
      <div className="absolute inset-0 -z-10">
        <img
          src={IMAGES.heroBg}
          alt="Engineering students at MIETW"
          className="w-full h-full object-cover"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-0 grid-pattern opacity-30 mix-blend-overlay" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
        {/* Left */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-7 text-white"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass-dark text-xs font-semibold tracking-wider uppercase border-white/20" data-testid="hero-accreditation">
            <ShieldCheck className="w-3.5 h-3.5 text-megha-highlight" />
            <span className="text-blue-50">{COLLEGE.accreditation}</span>
          </div>

          <h1 className="mt-5 font-heading font-black tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl xl:text-[68px]">
            B.Tech Admissions <br className="hidden sm:block" />
            <span className="relative inline-block">
              Open 2026
              <span className="absolute -bottom-2 left-0 right-0 h-2 bg-megha-highlight/80 rounded-full" />
            </span>
          </h1>

          <p className="mt-6 text-lg md:text-xl text-blue-50/90 max-w-2xl font-medium">
            {COLLEGE.tagline} — A premier women-focused engineering institution in {COLLEGE.location}.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            {COLLEGE.codes.map((c) => (
              <span
                key={c}
                className="px-3 py-1 rounded-full bg-white/10 border border-white/15 text-[11px] font-bold tracking-wider uppercase text-blue-50"
              >
                {c} — <span className="text-megha-highlight">{COLLEGE.codeWord}</span>
              </span>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#programs">
              <Button
                data-testid="btn-explore-programs"
                className="h-12 rounded-full bg-megha-highlight text-megha-text font-bold px-6 hover:opacity-95 shadow-lg shadow-yellow-500/30 hover:-translate-y-0.5 transition-all"
              >
                Explore Programs <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <Button
              data-testid="btn-download-brochure"
              onClick={onBrochure}
              variant="outline"
              className="h-12 rounded-full bg-white/10 border-white/40 text-white hover:bg-white hover:text-megha-primary font-bold px-6 transition-all"
            >
              <Download className="w-4 h-4 mr-2" /> Download Brochure
            </Button>
          </div>

          {/* Floating placement badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="mt-10 inline-flex items-center gap-4 px-5 py-4 rounded-2xl glass-dark animate-pulse-glow border-white/20"
            data-testid="hero-placement-badge"
          >
            <div className="w-12 h-12 rounded-xl bg-megha-highlight flex items-center justify-center">
              <Award className="w-6 h-6 text-megha-primary" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-blue-100/80">Placements 2025</div>
              <div className="text-2xl font-heading font-extrabold text-white">
                Highest Package <span className="text-megha-highlight">{COLLEGE.highestPackage}</span>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Right - Inquiry form */}
        <motion.div
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2, duration: 0.7 }}
          className="lg:col-span-5"
        >
          <div className="relative">
            <div className="absolute -top-4 -right-4 px-3 py-1.5 rounded-full bg-megha-highlight text-megha-text text-[11px] font-extrabold tracking-wider uppercase shadow-lg shadow-yellow-500/30 z-10 flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Limited Seats
            </div>
            <InquiryForm variant="glass" />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
